import { useStore } from '../store/StoreContext.jsx';
import Field from './Field.jsx';
import { SectionShell } from './FormSection.jsx';
import { SF_TEXT_LIMITS } from '../schema/fieldLimits.js';
import { getOwnerNameOptions } from '../store/selectors.js';
import DataTable from './DataTable.jsx';

const STATUS_OPTIONS = ['Employed', 'Self Employed', 'Retired', 'Not Employed'];

const EMPLOYED_FIELDS = [
  { key: 'jobTitle', label: 'Job Title', width: 'medium', maxLength: SF_TEXT_LIMITS['PQ_Employment_Info__c.Job_Title__c'] },
  { key: 'employer', label: 'Employer', width: 'medium', maxLength: SF_TEXT_LIMITS['PQ_Employment_Info__c.Employer__c'] },
  { key: 'numOfYears', label: '# of Years', type: 'number', width: 'field' },
  { key: 'retirementDate', label: 'Retirement Date', type: 'date', width: 'medium', dateRange: 'retirement' },
  // Derived from (and locked to) the Retirement Date while one is set; becomes an
  // editable number when the date is blank (see the per-field override below).
  { key: 'retirementAge', label: 'Retirement Age', type: 'computed', width: 'field' },
  { key: 'typeOfBusiness', label: 'Type of Business', width: 'medium' },
];

const NOT_EMPLOYED_FIELDS = [
  { key: 'lastJob', label: 'Last Job Title', width: 'medium' },
  { key: 'lastEmployer', label: 'Last Employer', width: 'medium' },
  { key: 'yearsWorked', label: 'Years Worked', type: 'number', width: 'field', integer: true },
  // Advisor feedback (Jake, 2026-07-23): for someone already retired the
  // question is "when DID you retire", so the same date field is shown here
  // too. The 'retirement' range spans 40 years either side of today, so a past
  // date is valid; toSalesforce sends it for non-employed statuses as well.
  { key: 'retirementDate', label: 'Retirement Date', type: 'date', width: 'medium', dateRange: 'retirement' },
];

// Employed + Self Employed share one field set; Retired + Not Employed share
// the other.
const isEmployedStatus = status =>
  status === 'Employed' || status === 'Self Employed';

function EmploymentSubsection({ title, clientKey }) {
  const { data, update } = useStore();
  const employment = data.employment || {};
  const personData = employment[clientKey] || {};
  // `||`, not `??`: a stored empty string has to fall back to the default too,
  // otherwise the section renders with NO status selected and the income row
  // that keys off the status disappears.
  const status = personData.status || 'Employed';

  // Self Employed uses the same fields as Employed; only the Employer field's
  // visible label changes to "Business Name". The stored key stays `employer`,
  // so the saved data and the Salesforce JSON are unchanged.
  const isEmployed = isEmployedStatus(status);
  // Type of Business describes a business the client runs, so it is asked only
  // of Self Employed — for a W-2 employee it was an empty box with nothing to
  // put in it (Jake, 2026-08-05).
  const fields = (isEmployed ? EMPLOYED_FIELDS : NOT_EMPLOYED_FIELDS)
    .filter(f => f.key !== 'typeOfBusiness' || status === 'Self Employed');

  // Changing status only swaps which field group is shown — it does NOT clear
  // the hidden fields. Entered values persist in form state, so a mis-clicked
  // status radio is fully recoverable: toggle back and the data is still there.
  // The Salesforce record still stays clean because toSalesforce omits/nulls the
  // fields that don't apply to the current status (see employmentRecord), so
  // inapplicable data never reaches Salesforce even while it lingers in the UI.

  return (
    <>
      <div className="subhead">{title}</div>
      <div className="choice-group" style={{ marginBottom: 10 }}>
        {STATUS_OPTIONS.map(opt => (
          <label
            key={opt}
            className={'choice' + (status === opt ? ' is-on' : '')}
          >
            <input
              type="radio"
              name={`employment.${clientKey}.status`}
              value={opt}
              checked={status === opt}
              onChange={() => update(`employment.${clientKey}.status`, opt)}
            />
            {opt}
          </label>
        ))}
      </div>
      <div className="grid">
        {fields.map(f => {
          // Visible-label-only override: relabel Employer as "Business Name"
          // for Self Employed. f.key (and thus the stored value + JSON) is
          // unchanged.
          let display =
            status === 'Self Employed' && f.key === 'employer'
              ? { ...f, label: 'Business Name' }
              : f;
          // Retirement Age mirrors the client Age field: derived and locked
          // while a Retirement Date is set, but editable when there's no date so
          // an advisor can capture a target age directly ("retire at 65").
          if (f.key === 'retirementAge' && !String(personData.retirementDate || '').trim()) {
            display = { ...display, type: 'number', min: 0, max: 120 };
          }
          return (
            <Field
              key={f.key}
              field={display}
              value={personData[f.key]}
              onChange={val => update(`employment.${clientKey}.${f.key}`, val)}
            />
          );
        })}
      </div>
    </>
  );
}

// Businesses the client or spouse owns or works for beyond the main job above,
// for the owner of several companies ("I'm talking to a prospect who is the CEO
// of 4 companies", Blaine Thiederman, 2026-09-07). Each row saves as one more
// employment record for that person. The income from each goes in Employment
// Income like any other salary.
function otherBusinessesTable(data) {
  // Employment belongs to one person, so Joint and Trust aren't offered.
  const owners = getOwnerNameOptions(data).filter(o => o !== 'Joint' && o !== 'Trust');
  return {
    key: 'otherBusinesses',
    title: 'Other Businesses',
    columns: [
      { key: 'owner', label: 'Owner', type: 'select', options: owners },
      { key: 'name', label: 'Business Name', colClass: 'col-wide', maxLength: SF_TEXT_LIMITS['PQ_Employment_Info__c.Business_Name__c'] },
      { key: 'role', label: 'Role', maxLength: SF_TEXT_LIMITS['PQ_Employment_Info__c.Job_Title__c'] },
      { key: 'typeOfBusiness', label: 'Type of Business', maxLength: SF_TEXT_LIMITS['PQ_Employment_Info__c.Type_of_Business__c'] },
      { key: 'numOfYears', label: '# of Years', type: 'number', integer: true },
    ],
  };
}

export default function EmploymentSection({ section, num }) {
  const { data } = useStore();
  const hasSpouse = !!data?._flags?.hasSpouse;

  return (
    <SectionShell section={section} num={num}>
      <EmploymentSubsection title="Client" clientKey="client1" />
      {hasSpouse && (
        <EmploymentSubsection title="Spouse / Partner" clientKey="client2" />
      )}
      <div className="employment-other">
        <DataTable sectionId="employment" tableDef={otherBusinessesTable(data)} />
        <p className="employment-other-hint">
          For a client with more than one business. Add the income from each one
          under Employment Income.
        </p>
      </div>
    </SectionShell>
  );
}
